import { jsxs, jsx } from 'react/jsx-runtime';
import { isNil } from '@flex-design-system/fx-utils/common';
import { getYear, format, isSameYear } from 'date-fns';
import { useState, useEffect } from 'react';
import { Button } from '../../../button/button/Button.mjs';
import getLocaleData from '../../../locale/getLocaleData.mjs';
import { useLinearLocale } from '../../../locale/LocaleContext.mjs';
import { useDatePickerContext } from '../../context/datePickerContext.mjs';
import { isBeforeYear, isAfterYear, isAfterOrSameYear, isBeforeOrSameYear } from '../../utils/date.mjs';
import { getStartOfYearDateFromYear } from '../../utils/getStartOfYearDateFromYear.mjs';
import Header from './Header.mjs';
import { wrapper, yearButton } from './yearCalendar.vanilla.mjs';

const DATE_PICKER_YEAR_CALENDAR_NAME = "DatePickerYearCalendar";
function getStartYear(year) {
  return year - (year % 12 === 0 ? 12 : year % 12) + 1;
}
function YearCalendar({ __scopeDatePicker }) {
  const [hoverYear, setHoverYear] = useState(void 0);
  const {
    mode,
    minDate,
    maxDate,
    startDate,
    endDate,
    selectedDate,
    onDateChange,
    onStartDateChange,
    onEndDateChange,
    excludeDates = [],
    openToDate
  } = useDatePickerContext(DATE_PICKER_YEAR_CALENDAR_NAME, __scopeDatePicker);
  const [baseYear, setBaseYear] = useState(() => {
    let _baseYear = null;
    if (openToDate !== void 0) {
      _baseYear = getYear(openToDate);
    } else if (mode === "range" && startDate !== null) {
      _baseYear = getYear(startDate);
    } else if (selectedDate !== null) {
      _baseYear = getYear(selectedDate);
    }
    return _baseYear ?? getYear(Date.now());
  });
  const { locale } = useLinearLocale();
  const dateFnsLocaleData = getLocaleData(locale);
  const isRangeMode = mode === "range";
  useEffect(() => {
    if (selectedDate !== null) {
      setBaseYear(getYear(selectedDate));
    }
    if (isRangeMode && endDate !== null) {
      setBaseYear(getYear(endDate));
    }
  }, [selectedDate, endDate, isRangeMode]);
  const getIsSelected = (year) => {
    const compareDate = getStartOfYearDateFromYear(year);
    if (isRangeMode) {
      return getIsRangeStartDate(year) || getIsRangeEndDate(year);
    } else {
      return selectedDate !== null && isSameYear(selectedDate, compareDate);
    }
  };
  const getIsRangeStartDate = (year) => {
    const compareDate = getStartOfYearDateFromYear(year);
    if (isRangeMode && startDate !== null) {
      return isSameYear(startDate, compareDate);
    }
    return false;
  };
  const getIsRangeEndDate = (year) => {
    const compareDate = getStartOfYearDateFromYear(year);
    if (isRangeMode && endDate !== null) {
      return isSameYear(endDate, compareDate);
    }
    return false;
  };
  const getIsInRange = (year) => {
    if (!isRangeMode) {
      return false;
    }
    const compareDate = getStartOfYearDateFromYear(year);
    if (startDate !== null && endDate !== null) {
      return isAfterOrSameYear(compareDate, startDate) && isBeforeOrSameYear(compareDate, endDate);
    }
    if (startDate !== null && endDate === null && hoverYear !== void 0) {
      const hoveredYearDate = getStartOfYearDateFromYear(hoverYear);
      return isAfterYear(compareDate, startDate) && isBeforeOrSameYear(compareDate, hoveredYearDate);
    }
  };
  const handleChangeYear = (year) => {
    const compareDate = getStartOfYearDateFromYear(year);
    if (mode === "single") {
      onDateChange(compareDate);
    } else if (isRangeMode) {
      if (startDate !== null && endDate !== null) {
        onStartDateChange(compareDate);
        onEndDateChange(null);
        return;
      }
      if (startDate === null || isBeforeYear(compareDate, startDate)) {
        onStartDateChange(compareDate);
      } else if (isAfterOrSameYear(compareDate, startDate)) {
        onEndDateChange(compareDate);
      }
    }
  };
  const handleIncreaseYear = () => {
    const newYear = baseYear + 12;
    setBaseYear(newYear);
  };
  const handleDecreaseYear = () => {
    const newYear = baseYear - 12;
    setBaseYear(newYear);
  };
  const getIsDisabled = (year) => {
    const compareDate = getStartOfYearDateFromYear(year);
    const isExcludedYear = excludeDates.find((excludeDate) => isSameYear(compareDate, excludeDate)) !== void 0;
    const isUnderMinYear = !isNil(minDate) && isBeforeYear(compareDate, minDate);
    const isOverMaxYear = !isNil(maxDate) && isAfterYear(compareDate, maxDate);
    return isExcludedYear || isUnderMinYear || isOverMaxYear;
  };
  const handleMouseEnter = (year) => {
    setHoverYear(year);
  };
  const handleMouseLeave = () => {
    setHoverYear(void 0);
  };
  return /* @__PURE__ */ jsxs("div", { style: { padding: "10px" }, children: [
    /* @__PURE__ */ jsx(
      Header,
      {
        startYear: getStartYear(baseYear),
        onClickNextYear: handleIncreaseYear,
        onClickPrevYear: handleDecreaseYear
      }
    ),
    /* @__PURE__ */ jsx("div", { className: wrapper, children: Array.from({ length: 12 }).map((_, yearIndex) => {
      const year = getStartYear(baseYear) + yearIndex;
      const disabled = getIsDisabled(year);
      const buttonProps = {
        key: year,
        children: format(
          getStartOfYearDateFromYear(year),
          locale === "ko" ? "yyyy\uB144" : "yyyy",
          {
            locale: dateFnsLocaleData
          }
        )
      };
      const isSelected = getIsSelected(year);
      return disabled ? /* @__PURE__ */ jsx(
        Button,
        {
          ...buttonProps,
          className: yearButton(),
          disabled: true,
          variant: "ghost",
          color: "grayDarker",
          "data-selected": isSelected
        }
      ) : /* @__PURE__ */ jsx(
        Button,
        {
          ...buttonProps,
          className: yearButton({
            isRangeStartDate: getIsRangeStartDate(year),
            isRangeEndDate: getIsRangeEndDate(year),
            isInRange: getIsInRange(year)
          }),
          variant: isSelected ? "solid" : "ghost",
          color: isSelected ? "green" : "grayDarker",
          "data-selected": isSelected,
          onClick: () => handleChangeYear(year),
          onPointerEnter: () => handleMouseEnter(year),
          onPointerLeave: handleMouseLeave
        }
      );
    }) })
  ] });
}

export { YearCalendar as default };
//# sourceMappingURL=index.mjs.map
